import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Clock, Crown, Sparkles } from "lucide-react";

const BottomLineSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const columns = [
    {
      icon: ShieldCheck,
      heading: "Instant Credibility",
      body: "In 2024, no website = no trust. It is your digital proof of legitimacy.",
      gradient: "from-primary/20 to-primary/5",
      glowColor: "primary",
    },
    {
      icon: Clock,
      heading: "24/7 Salesperson",
      body: "It captures leads and answers FAQs while you sleep. It never takes a sick day.",
      gradient: "from-accent/20 to-accent/5",
      glowColor: "accent",
    },
    {
      icon: Crown,
      heading: "Total Control",
      body: "Stop fighting algorithms for attention. Own your land and control the journey from start to sale.",
      gradient: "from-primary/20 to-accent/10",
      glowColor: "primary",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-6 md:py-12 bg-gradient-to-b from-background to-card relative overflow-hidden"
    >
      {/* Enhanced background with animated gradient orbs */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Header with sparkle accent */}
          <div
            className={`text-center mb-4 md:mb-8 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-full mb-3">
              <Sparkles size={14} className="text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-wider">
                The Bottom Line
              </span>
            </div>
            <h2 className="text-lg md:text-3xl lg:text-4xl font-black mb-2 md:mb-3 leading-tight">
              Is a Professional Website Still Worth It?{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">(Spoiler: Yes.)</span>
            </h2>
            <p className="text-xs md:text-base text-muted-foreground max-w-2xl mx-auto">
              Social media platforms are landlords. They change algorithms and hide your content.{" "}
              <span className="text-foreground font-medium">
                A website is the only digital real estate you actually own.
              </span>
            </p>
          </div>

          {/* Mobile: 2-column grid (Card 1 & 2 side-by-side, Card 3 full-width) */}
          <div className="md:hidden grid grid-cols-2 gap-2.5 mb-4">
            {columns.slice(0, 2).map((col, index) => (
              <div
                key={col.heading}
                className={`group relative bg-[#121212] border border-border/50 rounded-xl p-3 transition-all duration-500 ease-out hover:border-primary/50 hover:-translate-y-1 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${150 + index * 100}ms` }}
              >
                {/* Hover glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${col.gradient} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative z-10">
                  {/* Icon with glow */}
                  <div className="relative inline-flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg mb-2 group-hover:bg-primary/20 transition-colors">
                    <col.icon className="w-4 h-4 text-primary transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                  </div>

                  <h3 className="text-xs font-black text-foreground mb-1">
                    {col.heading}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-snug">
                    {col.body}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Card 3 - Full width */}
            <div
              className={`group relative col-span-2 bg-[#121212] border border-border/50 rounded-xl p-3 transition-all duration-500 ease-out hover:border-primary/50 hover:-translate-y-1 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "350ms" }}
            >
              {/* Hover glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${columns[2].gradient} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              <div className="relative z-10">
                <div className="relative inline-flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg mb-2 group-hover:bg-primary/20 transition-colors">
                  <Crown className="w-4 h-4 text-primary transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                </div>

                <h3 className="text-xs font-black text-foreground mb-1">
                  {columns[2].heading}
                </h3>

                <p className="text-sm text-muted-foreground leading-snug">
                  {columns[2].body}
                </p>
              </div>
            </div>
          </div>

          {/* Desktop: Three Columns Grid - Enhanced */}
          <div className="hidden md:grid md:grid-cols-3 gap-5 mb-8">
            {columns.map((col, index) => (
              <div
                key={col.heading}
                className={`group relative bg-[#121212] border border-border/50 rounded-2xl p-5 text-center transition-all duration-500 ease-out hover:border-primary/50 hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--primary)/0.3)] ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${150 + index * 100}ms` }}
              >
                {/* Animated gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${col.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                {/* Inner glow ring */}
                <div className="absolute inset-[1px] rounded-2xl bg-[#121212] z-0" />
                
                <div className="relative z-10">
                  {/* Icon with animated glow */}
                  <div className="relative inline-flex items-center justify-center w-14 h-14 mb-4">
                    {/* Pulsing glow behind icon */}
                    <div className="absolute inset-0 bg-primary/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-500" />
                    <div className="relative w-12 h-12 flex items-center justify-center bg-primary/10 rounded-xl group-hover:bg-primary/20 transition-all duration-300">
                      <col.icon className="w-6 h-6 text-primary transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_8px_hsl(var(--primary)/0.5)]" strokeWidth={1.5} />
                    </div>
                  </div>

                  <h3 className="text-lg font-black text-foreground mb-2 transition-colors duration-300 group-hover:text-primary">
                    {col.heading}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {col.body}
                  </p>
                </div>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-2xl" />
              </div>
            ))}
          </div>

          {/* Enhanced Conclusion Box */}
          <div
            className={`text-center transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            style={{ transitionDelay: "500ms" }}
          >
            <div className="relative inline-block group">
              {/* Animated border glow */}
              <div className="absolute -inset-[1px] bg-gradient-to-r from-primary via-accent to-primary rounded-xl opacity-50 blur-sm group-hover:opacity-100 group-hover:blur-md transition-all duration-500 animate-[pulse_3s_ease-in-out_infinite]" />
              
              <p className="relative text-xs md:text-lg font-bold text-foreground bg-[#121212] border border-border/50 rounded-xl px-4 md:px-8 py-3 md:py-5">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent font-black">Conclusion:</span> A website isn't an expense.{" "}
                It's the hub that makes all your other marketing{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 font-black">actually work.</span>
              </p>
            </div>
            
            {/* Subtle caption */}
            <p className="mt-3 text-[10px] md:text-xs text-muted-foreground italic">
              Invest in an asset you actually own.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BottomLineSection;
